import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useImperativeHandle } from "react";
import { styled } from "../styled";
import { Label } from "../typography";
import { InputVariant } from "./input";
/**
 * Container Element
 */
const ContainerElement = styled("div", ({ fullWidth, fontSize }) => ({
    width: fullWidth === true ? "100%" : "min(300px, calc(100% - 20px))",
    fontSize: fontSize ?? 14,
    fontFamily: 'hiyllo, sans-serif',
}));
/**
 * Input Element
 */
const InputElement = styled("input", ({ $theme }) => ({
    outline: "none",
    border: "none",
    colorScheme: $theme.colorScheme,
    width: "100%",
    display: "block",
    fontSize: "inherit",
    background: "transparent",
    color: $theme.foreground,
    resize: "none",
    fontFamily: "inherit",
    padding: 0,
}));
/**
 * TextArea Element
 */
const TextAreaElement = styled("textarea", ({ $theme }) => ({
    outline: "none",
    border: "none",
    colorScheme: $theme.colorScheme,
    width: "100%",
    display: "block",
    fontSize: "inherit",
    background: "transparent",
    color: $theme.foreground,
    resize: "none",
    fontFamily: "inherit",
    padding: 0,
}));
const InputContainer = styled("div", ({ $theme, $variant }) => ({
    background: $theme.midground,
    color: $theme.foreground,
    borderRadius: $variant === InputVariant.search ? 40 : 10,
    display: "flex",
    flexDirection: "row",
    transition: "box-shadow cubic-bezier(0.1, 0.12, 0.1, 0.95) 0.3s",
    ":hover": {
        boxShadow: "0px 0px 15px 5px rgba(0, 0, 0, 0.5)"
    },
    ":focus": {
        boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.5)"
    }
}));
const StartEnhancer = styled("div", ({ $theme }) => ({
    background: $theme.midground,
    borderRadius: "inherit",
    paddingLeft: 11,
    height: 40,
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    userSelect: "none",
}));
const EndEnhancer = styled("div", ({ $theme }) => ({
    background: $theme.midground,
    borderRadius: "inherit",
    paddingRight: 11,
    height: 40,
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    userSelect: "none",
}));
const InputElementContainer = styled("div", ({ $theme, $hasStartEnhancer, $hasEndEnhancer, $variant }) => ({
    minHeight: 25,
    paddingTop: 7.5,
    paddingBottom: 7.5,
    paddingLeft: $hasStartEnhancer ? 0 : $variant === InputVariant.search ? 15 : 11,
    paddingRight: $hasEndEnhancer ? 0 : $variant === InputVariant.search ? 15 : 11,
    width: 0,
    flexGrow: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
}));
export const InputWeb = React.memo(React.forwardRef(function InputWeb(props, ref) {
    const internalRef = React.useRef(null);
    useImperativeHandle(ref, () => internalRef.current);
    const refreshMutlilineHeightIfNeeded = React.useCallback(() => {
        if (props.multiline === true && internalRef.current != null) {
            const target = internalRef.current;
            target.style.height = "0px";
            target.style.height = `${target.scrollHeight}px`;
        }
    }, []);
    const onChange = React.useCallback((evt) => {
        refreshMutlilineHeightIfNeeded();
        if (props.regexp != null && !props.regexp.test(evt.target.value) && evt.target.value !== "") {
            return;
        }
        void props.onChangeValue?.(evt.target.value);
        if (props.onChange != null) {
            props.onChange(evt);
        }
    }, [props.onChange]);
    const onKeyDown = React.useCallback((evt) => {
        if (evt.key === "Enter" && !evt.shiftKey) {
            void props.onSubmit?.();
        }
        if (props.onKeyDown != null) {
            props.onKeyDown(evt);
        }
    }, [props.onKeyDown, props.onSubmit]);
    React.useEffect(() => {
        refreshMutlilineHeightIfNeeded();
    }, [props.value]);
    const currentMLValue = React.useRef(null);
    React.useEffect(() => {
        if (currentMLValue.current === null) {
            currentMLValue.current = props.multiline ?? false;
            return;
        }
        if (currentMLValue.current !== (props.multiline ?? false)) {
            throw new Error(`[@hiyllo/ux]: props.multiline is not a dynamic value and should not be changed. To switch an Input between multiline and non-multiline, use a hard switch between two separate elements`);
        }
    }, [props.multiline]);
    const Element = props.multiline === true ? TextAreaElement : InputElement;
    const inputStyle = React.useMemo(() => ({
        ...props.inputStyle,
        textWrap: props.multiline ? null : "nowrap",
    }), [props.inputStyle, props.multiline]);
    return (_jsxs(ContainerElement, { fullWidth: props.fullWidth, fontSize: props.fontSize, style: props.outerStyle, children: [props.label ? _jsx(Label, { children: props.label }) : null, _jsx("label", { children: _jsxs(InputContainer, { style: props.containerStyle, "$variant": props.variant, children: [props.startEnhancer != null ? (_jsx(StartEnhancer, { children: props.startEnhancer })) : null, _jsx(InputElementContainer, { "$hasStartEnhancer": props.startEnhancer != null, "$hasEndEnhancer": props.endEnhancer != null, "$variant": props.variant, children: _jsx(Element, { _ref: internalRef, style: inputStyle, placeholder: props.placeholder, defaultValue: props.defaultValue, value: props.value, id: props.id, onChange: onChange, onClick: props.onClick, onPaste: props.onPaste, onKeyDown: onKeyDown, onKeyUp: props.onKeyUp, onBlur: props.onBlur, disabled: props.disabled, type: props.type, rows: 1, autoFocus: props.autoFocus }) }), props.endEnhancer != null ? (_jsx(EndEnhancer, { children: props.endEnhancer })) : null] }) })] }));
}));
